import {Input, Popover} from 'antd';
import {TextAreaRef} from 'antd/lib/input/TextArea';
import {MutableRefObject, useCallback, useRef} from 'react';

import {classNames} from '../../../../util/css';
import {ResponseTemplateSelect} from '../../response-templates/response-template-select/response-template-select';

import {ReviewReplyEmojiPicker} from './review-reply-emoji-picker';
import {
    getSelectionRangeFromTextArea,
    insertTextAtSelection,
    restoreTextAreaCursor,
    TextAreaSelectionRangeType,
} from './review-reply-emoji-picker-helper';
import {ReviewReplyTextAreaWithEmojiPropsType} from './review-reply-emoji-picker-types';
import * as styles from './review-reply-emoji-picker.module.scss';

function getSelectionRangeAtEnd(textLength: number): TextAreaSelectionRangeType {
    return {
        end: textLength,
        start: textLength,
    };
}

export function ReviewReplyTextAreaWithEmoji(props: ReviewReplyTextAreaWithEmojiPropsType): JSX.Element {
    const {disabled, onValueChange, quickTemplatePopover, textareaClassName, textareaRef, value, ...textareaProps} =
        props;

    const cursorRestoreRef = useRef<TextAreaRef | null>(null);
    const valueRef = useRef(value);

    valueRef.current = value;

    function setTextareaRef(instance: TextAreaRef | null): void {
        cursorRestoreRef.current = instance;

        if (textareaRef) {
            (textareaRef as MutableRefObject<TextAreaRef | null>).current = instance;
        }
    }

    const handleEmojiSelect = useCallback(
        (emoji: string): void => {
            const textarea = cursorRestoreRef.current?.resizableTextArea?.textArea;
            const selectionRange = textarea
                ? getSelectionRangeFromTextArea(textarea)
                : getSelectionRangeAtEnd(valueRef.current.length);
            const {cursorPosition, value: nextValue} = insertTextAtSelection(valueRef.current, emoji, selectionRange);

            onValueChange(nextValue);

            if (!textarea) {
                return;
            }

            requestAnimationFrame(() => {
                restoreTextAreaCursor(textarea, cursorPosition);
            });
        },
        [onValueChange]
    );

    const textareaElement = (
        <Input.TextArea
            {...textareaProps} // eslint-disable-line react/jsx-props-no-spreading
            className={classNames(styles.ReviewReplyEmojiPicker_textarea, textareaClassName)}
            disabled={disabled}
            ref={setTextareaRef}
            value={value}
        />
    );

    if (!quickTemplatePopover) {
        return (
            <div className={styles.ReviewReplyEmojiPicker_textareaWrap}>
                {textareaElement}
                <ReviewReplyEmojiPicker disabled={disabled} onEmojiSelect={handleEmojiSelect} />
            </div>
        );
    }

    const {
        isLoading: quickTemplateIsLoading,
        isOpen: quickTemplateIsOpen,
        onClickOutside: handleQuickTemplateClickOutside,
        onOpenChange: handleQuickTemplateOpenChange,
        onTemplateSelect: handleQuickTemplateSelect,
        templates: quickTemplateList,
    } = quickTemplatePopover;

    return (
        <div className={styles.ReviewReplyEmojiPicker_textareaWrap}>
            <Popover
                content={
                    <ResponseTemplateSelect
                        isLoading={quickTemplateIsLoading}
                        isOpen={quickTemplateIsOpen}
                        onClickOutside={handleQuickTemplateClickOutside}
                        onTemplateSelect={handleQuickTemplateSelect}
                        templates={quickTemplateList}
                    />
                }
                onOpenChange={handleQuickTemplateOpenChange}
                open={quickTemplateIsOpen}
                placement="topLeft"
                trigger={[]}
            >
                {textareaElement}
            </Popover>
            <ReviewReplyEmojiPicker disabled={disabled} onEmojiSelect={handleEmojiSelect} />
        </div>
    );
}
